import { useMemo, useState } from 'react';
import { Autocomplete, Link, Pagination, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, isBefore } from 'date-fns';
import {
    getAllDestinationsFromOrigine,
    getAllJourneysWithFilters,
} from '../services/ApiService';
import { ApiResponse, Journey, Station } from '../utils/types';
import StationCard from './StationCard';
import CustomModal from './CustomModal';
import ItineraryCard from './ItineraryCard';

import allStationsAvailable from '../data/liste-des-gares.json';

export default function Hero() {
    const [origineStation, setOrigineStation] = useState<Station>({
        iata: '',
        name: '',
    });
    const [destinationStation, setDestinationStation] = useState<Station>({
        iata: '',
        name: '',
    });
    const [departureDate, setDepartureDate] = useState<Date>(new Date());
    const [returnDate, setReturnDate] = useState<Date | null>(null);
    const [stations, setStations] = useState<Station[]>([]);

    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [journeys, setJourneys] = useState<Journey[]>([]);

    const [page, setPage] = useState(1);

    const minDate = useMemo(() => new Date(), []);
    const maxDate = useMemo(() => addDays(new Date(), 30), []);

    const journeysList = useMemo(() => {
        return journeys.map((journey: Journey, index: number) => {
            return (
                <ItineraryCard index={index} itinerary={journey} key={index} />
            );
        });
    }, [journeys]);

    const stationCardList = useMemo(() => {
        const handleFetchAllJourneyBetweenTwoStations = (
            arrivalStation: Station
        ) => {
            getAllJourneysWithFilters({
                origineStation: origineStation.iata,
                destinationStation: arrivalStation.iata,
                departureDate: departureDate,
                returnDate: returnDate || undefined,
            }).then((response: ApiResponse<Journey>) =>
                setJourneys(response.results)
            );
        };

        const handleOnClickStationCard = (stationClicked: Station) => {
            setDestinationStation(stationClicked);
            handleFetchAllJourneyBetweenTwoStations(stationClicked);
            setIsModalOpen(true);
        };

        return stations.map((station, index) => {
            return (
                <StationCard
                    station={station}
                    index={index}
                    key={index}
                    handleOnClick={handleOnClickStationCard}
                />
            );
        });
    }, [stations, origineStation, departureDate, returnDate]);

    const clearStations = () => setStations([]);
    const clearJourneys = () => setJourneys([]);
    const resetPage = () => setPage(1);

    const handleSearch = () => {
        setLoading(true);
        clearStations();
        resetPage();
        getAllDestinationsFromOrigine(
            origineStation?.iata,
            departureDate,
            returnDate || undefined
        )
            .then((data) => {
                setStations(data.results);
            })
            .finally(() => setLoading(false));
    };

    const handleOrigineStationChange = (newOrigineValue: {
        code_iata: string;
        station_name: string;
    }) => {
        setOrigineStation({
            iata: newOrigineValue.code_iata,
            name: newOrigineValue.station_name,
        });
    };

    return (
        <Box
            id='hero'
            sx={(theme) => ({
                width: '100%',
                maxWidth: '100%',
                backgroundImage:
                    theme.palette.mode === 'light'
                        ? 'linear-gradient(180deg, #CEE5FD, #FFF)'
                        : `linear-gradient(#02294F, ${alpha('#090E10', 0.0)})`,
                backgroundSize: '100% 20%',
                backgroundRepeat: 'no-repeat',
            })}>
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: { xs: 14, sm: 20 },
                    pb: { xs: 8, sm: 12 },
                }}>
                <Stack
                    spacing={2}
                    useFlexGap
                    sx={{ width: { xs: '100%', sm: '70%' } }}>
                    <Typography
                        variant='h1'
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            alignSelf: 'center',
                            textAlign: 'center',
                            fontSize: 'clamp(3.5rem, 10vw, 4rem)',
                        }}>
                        Votre prochaine&nbsp;
                        <Typography
                            component='span'
                            variant='h1'
                            sx={{
                                fontSize: 'clamp(3rem, 10vw, 4rem)',
                                color: (theme) =>
                                    theme.palette.mode === 'light'
                                        ? 'primary.main'
                                        : 'primary.light',
                            }}>
                            escapade
                        </Typography>
                    </Typography>
                    <Typography
                        variant='subtitle1'
                        textAlign='center'
                        color='text.secondary'
                        sx={{
                            alignSelf: 'center',
                            width: { sm: '100%', md: '80%' },
                        }}>
                        Trouver votre prochaine escapade en train en renseignant
                        votre gare de départ, une date de départ et
                        optionnellement une date de retour.
                        <br />
                    </Typography>
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        fontStyle='italic'>
                        (actuellement, seules les destinations françaises et
                        accessibles avec l'abonnement{' '}
                        <Link
                            href='https://www.maxjeune-tgvinoui.sncf/sncf-connect'
                            target='_blank'>
                            TGV Max
                        </Link>{' '}
                        de la SNCF sont disponibles)
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignSelf='center'
                        spacing={1}
                        useFlexGap
                        sx={{ pt: 2, width: { xs: '100%', sm: 'auto' } }}>
                        <Autocomplete
                            id='origine-station'
                            size='small'
                            aria-label='Entrez une gare de départ'
                            options={allStationsAvailable}
                            getOptionLabel={(option) =>
                                option.station_name || 'Aucune gare trouvée'
                            }
                            onChange={(event, newValue) => {
                                handleOrigineStationChange(
                                    newValue
                                        ? newValue
                                        : { code_iata: '', station_name: '' }
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    key={params.id}
                                    label='Gare de départ'
                                />
                            )}
                        />
                        <DatePicker
                            label='Date de départ'
                            value={departureDate}
                            minDate={minDate}
                            maxDate={maxDate}
                            onChange={(newValue) => {
                                if (newValue) {
                                    setDepartureDate(newValue);
                                    if (
                                        returnDate &&
                                        isBefore(returnDate, newValue)
                                    ) {
                                        setReturnDate(newValue);
                                    }
                                }
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    hiddenLabel: true,
                                    'aria-label': 'Entrez une date de départ',
                                },
                                actionBar: {
                                    actions: ['clear', 'today'],
                                },
                                field: {
                                    clearable: false,
                                },
                            }}
                        />
                        <DatePicker
                            label='Date de retour (optionnel)'
                            value={returnDate}
                            minDate={departureDate}
                            maxDate={maxDate}
                            onChange={(newValue) => {
                                setReturnDate(newValue);
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    hiddenLabel: true,
                                    'aria-label':
                                        'Entrez une date de retour (optionnel)',
                                },
                                actionBar: {
                                    actions: ['clear', 'today'],
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                        {origineStation.name !== '' && (
                            <Button
                                id='search-button'
                                variant='contained'
                                color='primary'
                                onClick={handleSearch}
                                disabled={
                                    loading || origineStation.name === ''
                                }>
                                Rechercher
                            </Button>
                        )}
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        width: { ws: '100%', sm: '600px' },
                        mt: 3,
                    }}>
                    <Typography variant='subtitle1'>
                        <b>
                            {loading
                                ? 'Réflexion en cours pour trouver des destinations...'
                                : stations?.length
                                ? new Intl.DateTimeFormat('fr-FR').format(
                                      departureDate
                                          ? new Date(departureDate)
                                          : new Date()
                                  )
                                : 'Aucune escapade trouvée'}
                        </b>
                    </Typography>
                </Box>
                {stationCardList?.length > 0 && (
                    <Box maxWidth={'100%'}>
                        {stationCardList.slice((page - 1) * 10, page * 10)}
                        <Pagination
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            count={Math.ceil(stationCardList.length / 10)}
                            boundaryCount={1}
                            siblingCount={1}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '1em',
                            }}
                        />
                    </Box>
                )}
            </Container>
            <CustomModal
                isModalOpen={isModalOpen}
                title={
                    <Typography variant='h5' component='span'>
                        <Typography
                            variant='h5'
                            component='span'
                            color={(theme) =>
                                theme.palette.mode === 'light'
                                    ? 'primary.main'
                                    : 'primary.light'
                            }>
                            {origineStation.name}
                        </Typography>
                        {' vers '}
                        <Typography
                            variant='h5'
                            component='span'
                            color={(theme) =>
                                theme.palette.mode === 'light'
                                    ? 'primary.main'
                                    : 'primary.light'
                            }>
                            {destinationStation.name}
                        </Typography>
                    </Typography>
                }
                content={journeysList}
                handleOnClose={() => {
                    setIsModalOpen(false);
                    clearJourneys();
                }}
                shouldDisplayCloseBtn={false}
                shouldDisplayValidateBtn={false}
            />
        </Box>
    );
}
